import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, CheckCircle, Target, TrendingUp, BarChart3, 
  Zap, Shield, Eye, Database, LineChart, Bot, Sparkles,
  Search, Share2, Monitor, Video, Globe, Users, Award,
  ChevronDown, Brain, Cpu, Activity, Layers, PieChart
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import FluidBackground from '../components/FluidBackground';

const AIDigitalMarketingAgency = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);

  const scrollToContact = () => {
    navigate('/', { state: { scrollTo: 'contact' } });
  };

  const problems = [
    { icon: Eye, title: "Broken Tracking", desc: "Campaigns running blind without proper conversion data or attribution clarity." },
    { icon: Database, title: "Weak Attribution", desc: "No clear understanding of which channels actually drive revenue." },
    { icon: Activity, title: "No Signal Optimization", desc: "Ad platforms starved of quality data, leading to poor algorithmic performance." },
    { icon: Shield, title: "Data Integrity Gaps", desc: "Decisions made on incomplete or inaccurate analytics foundations." },
    { icon: Target, title: "SEO Without Revenue Alignment", desc: "Content strategies disconnected from actual business outcomes." },
  ];

  const aiDifferences = [
    { icon: Bot, title: "AI-Driven Campaign Optimization", desc: "Machine learning algorithms that continuously optimize bids, audiences, and creative in real-time." },
    { icon: PieChart, title: "Predictive Budget Allocation", desc: "AI-powered forecasting that distributes spend across channels for maximum ROI." },
    { icon: Database, title: "GA4 + Server-Side Tracking", desc: "First-party data infrastructure that feeds clean signals to ad platforms." },
    { icon: LineChart, title: "Conversion Modeling", desc: "Statistical models that capture conversions even when tracking is limited." },
    { icon: Layers, title: "Full-Funnel Automation", desc: "Automated workflows that nurture leads from awareness to conversion." },
    { icon: Sparkles, title: "Data-Backed Creative Testing", desc: "Systematic A/B testing powered by AI insights to identify winning creative." },
  ];

  const services = [
    {
      title: "AI-Powered Paid Media",
      icon: Target,
      items: ["Google Ads with Smart Bidding optimization", "Meta Ads with Advantage+ campaigns", "Programmatic display & video", "Full-funnel strategy & attribution"],
      link: "/services/paid-media-advertising"
    },
    {
      title: "SEO & AI Search Optimization",
      icon: Search,
      items: ["Technical SEO audits & implementation", "AI-optimized content strategy", "Schema markup & structured data", "LLMO / Generative search visibility"],
      link: "/services/seo-optimization"
    },
    {
      title: "Conversion Rate Optimization",
      icon: TrendingUp,
      items: ["Landing page A/B testing", "Heatmap & session analysis", "Funnel optimization", "User experience improvements"],
      link: "/services/content-marketing"
    },
    {
      title: "Website & Creative",
      icon: Monitor,
      items: ["Performance-driven web design", "Video production for ads", "Creative built for paid media", "Brand identity & messaging"],
      link: "/services/video-production"
    },
  ];

  const differentiators = [
    { icon: Database, title: "Signal-First Strategy", desc: "We build your tracking infrastructure before spending a dollar on ads." },
    { icon: Activity, title: "GA4 Event Mapping", desc: "Custom event tracking aligned with your actual business objectives." },
    { icon: Shield, title: "Conversion API Setup", desc: "Server-side tracking that captures data browsers miss." },
    { icon: Users, title: "CRM Integration", desc: "Connect ad platforms directly to your sales data for true ROI tracking." },
    { icon: PieChart, title: "Attribution Modeling", desc: "Multi-touch attribution that shows the real customer journey." },
  ];

  const results = [
    { metric: "30-50%", label: "Reduction in CAC", desc: "Through signal optimization and audience modeling" },
    { metric: "2-3x", label: "Improved Lead Quality", desc: "Via AI-powered audience segmentation" },
    { metric: "40%+", label: "ROAS Improvement", desc: "Through structured testing frameworks" },
  ];

  const whyRansen = [
    { icon: Brain, title: "AI-First Framework", desc: "Every strategy built on data intelligence, not guesswork." },
    { icon: Layers, title: "Integrated SEO + Paid", desc: "Unified approach that compounds organic and paid growth." },
    { icon: Eye, title: "Transparent Reporting", desc: "Real-time dashboards with metrics that actually matter." },
    { icon: LineChart, title: "Strategic Forecasting", desc: "Predictive models that guide budget and resource allocation." },
    { icon: Globe, title: "Canada Market Expertise", desc: "Deep understanding of Canadian consumer behavior and regulations." },
  ];

  const faqs = [
    {
      q: "What makes an AI digital marketing agency different from traditional agencies?",
      a: "An AI digital marketing agency leverages machine learning and automation for campaign optimization, predictive analytics for budget allocation, and advanced tracking infrastructure to ensure ad platforms receive quality signals. This results in faster optimization cycles, better targeting, and measurable ROI improvements."
    },
    {
      q: "How does AI improve paid media performance?",
      a: "AI enhances paid media through automated bid optimization, predictive audience modeling, dynamic creative optimization, and real-time budget reallocation. Our AI systems continuously learn from campaign data to improve performance without manual intervention."
    },
    {
      q: "What tracking infrastructure do you implement?",
      a: "We implement GA4 with enhanced measurement, server-side tracking via Conversion APIs, custom event mapping aligned with business KPIs, CRM integrations for offline conversion tracking, and multi-touch attribution modeling."
    },
    {
      q: "Do you work with businesses outside of Canada?",
      a: "While we have deep expertise in the Canadian market, we work with businesses across North America. Our AI-driven approach and tracking infrastructure work effectively regardless of geographic location."
    },
    {
      q: "What industries do you specialize in?",
      a: "We work across B2B, eCommerce, SaaS, professional services, real estate, and healthcare. Our AI-first approach adapts to any industry where data-driven marketing can drive growth."
    },
  ];

  return (
    <div className="min-h-screen bg-void text-white">
      <SEO 
        title="AI Digital Marketing Agency in Canada | Ransen"
        description="Ransen is an AI-powered digital marketing agency delivering paid media, SEO, and conversion optimization backed by advanced tracking and performance intelligence."
        keywords="AI digital marketing agency, digital marketing agency, SEO agency, paid media agency, AI marketing, performance marketing, Canada digital marketing"
      />
      
      <Header />
      <FluidBackground />

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center pt-20 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-4 py-2 bg-brand/20 border border-brand/30 rounded-full text-sm font-accent uppercase tracking-wider text-brand mb-6">
              AI-Powered Growth Partner
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight"
          >
            <span className="bg-gradient-to-r from-brand via-accent-pink to-accent-cyan bg-clip-text text-transparent">AI Digital Marketing Agency</span>
            <br />
            <span className="text-white text-3xl sm:text-4xl md:text-5xl">Built for Performance-Driven Growth</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg md:text-xl text-white/70 max-w-3xl mx-auto mb-10 leading-relaxed"
          >
            Ransen combines <strong className="text-white">AI-powered media buying</strong>, <strong className="text-white">advanced tracking infrastructure</strong>, and <strong className="text-white">full-funnel strategy</strong> to help modern brands scale with clarity and measurable ROI.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Button 
              onClick={scrollToContact}
              className="bg-gradient-to-r from-brand to-accent-pink hover:opacity-90 text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg shadow-brand/30"
              data-testid="hero-cta"
            >
              Book a Growth Strategy Call
              <ArrowRight className="ml-2" size={20} />
            </Button>
            <Button 
              onClick={() => document.getElementById('approach').scrollIntoView({ behavior: 'smooth' })}
              className="bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-full px-8 py-6 text-lg font-semibold"
            >
              See Our Approach
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface relative">
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-white to-white/60 bg-clip-text text-transparent">
                Why Most Digital Marketing Agencies Underperform
              </span>
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto text-lg">
              The hard truth: most agencies are running campaigns on broken foundations. Here's what we see constantly:
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {problems.map((problem, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="p-6 bg-void/50 border border-red-500/20 hover:border-red-500/40 transition-all h-full">
                  <problem.icon className="text-red-400 mb-4" size={32} />
                  <h3 className="font-heading text-xl font-bold text-white mb-2">{problem.title}</h3>
                  <p className="text-white/60">{problem.desc}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Difference Section */}
      <section id="approach" className="py-20 px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-brand font-accent text-sm uppercase tracking-wider">The AI Advantage</span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold mt-4 mb-6">
              <span className="bg-gradient-to-r from-white to-white/60 bg-clip-text text-transparent">
                What Makes an AI Digital Marketing Agency Different?
              </span>
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto text-lg">
              We don't just run ads. We build intelligent systems that learn, adapt, and optimize continuously.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {aiDifferences.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="p-6 bg-surface/50 border border-brand/20 hover:border-brand/40 transition-all h-full group">
                  <div className="w-12 h-12 rounded-xl bg-brand/20 flex items-center justify-center mb-4 group-hover:bg-brand/30 transition-colors">
                    <item.icon className="text-brand" size={24} />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-white mb-2">{item.title}</h3>
                  <p className="text-white/60">{item.desc}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface relative">
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-brand font-accent text-sm uppercase tracking-wider">Core Services</span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold mt-4 mb-6">
              <span className="bg-gradient-to-r from-white to-white/60 bg-clip-text text-transparent">
                Full-Stack Growth Services
              </span>
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto text-lg">
              We're not just running ads — we're building growth systems that compound over time.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Card 
                  className="p-8 bg-void/50 border border-brand/20 hover:border-brand/40 transition-all cursor-pointer group"
                  onClick={() => navigate(service.link)}
                >
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 rounded-xl bg-brand/20 flex items-center justify-center group-hover:bg-brand/30 transition-colors">
                      <service.icon className="text-brand" size={28} />
                    </div>
                    <h3 className="font-heading text-2xl font-bold text-white group-hover:text-brand transition-colors">
                      {service.title}
                    </h3>
                  </div>
                  <ul className="space-y-3">
                    {service.items.map((item, j) => (
                      <li key={j} className="flex items-start gap-3">
                        <CheckCircle className="text-brand mt-0.5 flex-shrink-0" size={18} />
                        <span className="text-white/70">{item}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6 flex items-center text-brand font-medium group-hover:gap-2 transition-all">
                    Learn more <ArrowRight size={16} className="ml-1" />
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Data Infrastructure Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-brand font-accent text-sm uppercase tracking-wider">Our Differentiator</span>
              <h2 className="font-heading text-3xl md:text-5xl font-bold mt-4 mb-6">
                <span className="bg-gradient-to-r from-white to-white/60 bg-clip-text text-transparent">
                  Data Infrastructure Before Ad Spend
                </span>
              </h2>
              <p className="text-white/60 text-lg mb-8">
                Most agencies start spending immediately. We start by ensuring every dollar can be tracked, attributed, and optimized. <strong className="text-white">Tracking must be airtight before campaigns launch.</strong>
              </p>
              <Button 
                onClick={scrollToContact}
                className="bg-brand hover:bg-brand-dark text-white rounded-full px-6 py-3"
              >
                Get a Free Tracking Audit
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-4"
            >
              {differentiators.map((item, i) => (
                <div key={i} className="flex items-start gap-4 p-4 rounded-xl bg-surface/30 border border-brand/10 hover:border-brand/30 transition-all">
                  <div className="w-10 h-10 rounded-lg bg-brand/20 flex items-center justify-center flex-shrink-0">
                    <item.icon className="text-brand" size={20} />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-bold text-white">{item.title}</h3>
                    <p className="text-white/60 text-sm">{item.desc}</p>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface relative">
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-brand font-accent text-sm uppercase tracking-wider">Results-Focused</span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold mt-4 mb-6">
              <span className="bg-gradient-to-r from-white to-white/60 bg-clip-text text-transparent">
                Performance That Matters
              </span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {results.map((result, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center p-8 rounded-2xl bg-void/50 border border-brand/20"
              >
                <div className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-brand to-accent-pink bg-clip-text text-transparent mb-2">
                  {result.metric}
                </div>
                <h3 className="font-heading text-xl font-bold text-white mb-2">{result.label}</h3>
                <p className="text-white/60">{result.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Ransen Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-brand font-accent text-sm uppercase tracking-wider">Why Choose Us</span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold mt-4 mb-6">
              <span className="bg-gradient-to-r from-white to-white/60 bg-clip-text text-transparent">
                Why Ransen
              </span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyRansen.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="p-6 bg-surface/30 border border-brand/10 hover:border-brand/30 transition-all h-full">
                  <item.icon className="text-brand mb-4" size={28} />
                  <h3 className="font-heading text-lg font-bold text-white mb-2">{item.title}</h3>
                  <p className="text-white/60 text-sm">{item.desc}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface relative">
        <div className="max-w-3xl mx-auto relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-brand font-accent text-sm uppercase tracking-wider">FAQ</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mt-4">
              <span className="bg-gradient-to-r from-white to-white/60 bg-clip-text text-transparent">
                Frequently Asked Questions
              </span>
            </h2>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="rounded-xl border border-brand/10 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left bg-void/50 hover:bg-void/70 transition-colors"
                >
                  <span className="font-semibold text-white pr-4">{faq.q}</span>
                  <ChevronDown className={`text-brand transition-transform flex-shrink-0 ${openFaq === i ? 'rotate-180' : ''}`} size={20} />
                </button>
                {openFaq === i && (
                  <div className="px-6 py-4 bg-surface/50">
                    <p className="text-white/70">{faq.a}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-brand via-accent-pink to-accent-cyan bg-clip-text text-transparent">
                Ready to Scale With Smarter Marketing Infrastructure?
              </span>
            </h2>
            <p className="text-white/60 text-lg mb-10 max-w-2xl mx-auto">
              Stop wasting budget on broken foundations. Let's build a growth system that actually works.
            </p>
            <Button 
              onClick={scrollToContact}
              className="bg-gradient-to-r from-brand to-accent-pink hover:opacity-90 text-white rounded-full px-10 py-7 text-xl font-semibold shadow-xl shadow-brand/30"
              data-testid="final-cta"
            >
              Book a Growth Strategy Session
              <ArrowRight className="ml-2" size={24} />
            </Button>
          </motion.div>
        </div>
      </section>

      <Footer />

      {/* FAQ Schema for SEO */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map(faq => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.a
          }
        }))
      })}} />

      {/* Organization Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Ransen",
        "description": "AI-powered digital marketing agency delivering paid media, SEO, and conversion optimization.",
        "url": "https://ransen.ca",
        "logo": "https://ransen.ca/favicon.png",
        "address": {
          "@type": "PostalAddress",
          "addressRegion": "Ontario",
          "addressCountry": "CA"
        },
        "sameAs": [
          "https://www.instagram.com/ransenagency",
          "https://www.facebook.com/ransenagency",
          "https://www.linkedin.com/company/ransenagency"
        ]
      })}} />
    </div>
  );
};

export default AIDigitalMarketingAgency;
